import React, { ReactElement, useCallback, useMemo } from 'react';
import { InfobarType } from 'b2b-apps/shop/app/components/App/Infobars/Infobars/Infobar';
import { getCookie, setCookie } from 'b2b-apps/shop/common/utils/storage/cookies';
import { CONTENT_COLLECTION, useCollection } from 'b2b-apps/shop/content/hooks';
import { ContentCollectionEntryType } from 'b2b-apps/shop/content/redux/contentCollections/types';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

const COOKIE_GLOBAL_MSG = 'infobar_';

export enum CONTENT_INFOBAR_TYPE {
  error = 'error',
  warning = 'warning',
  success = 'success',
  info = 'info',
}

export const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement | null => {
    const content = useCollection(CONTENT_COLLECTION.infobar, { show_global_message: 'Yes' });
    const { data, isLoaded } = content;

    const onDismiss = useCallback((entryId: string) => {
      setCookie(
        `${COOKIE_GLOBAL_MSG}${entryId}`,
        { isVisible: false },
        7,
      );
    }, []);

    const getEntriesMappedIntoInfobars = useCallback(
      (entriesInfobars: ContentCollectionEntryType[]) => (
        contentInfobarType: CONTENT_INFOBAR_TYPE,
      ): InfobarType[] => entriesInfobars
        .filter(
          ({ data }: ContentCollectionEntryType) => data.type === contentInfobarType,
        )
        .map(({ data, entryId }: ContentCollectionEntryType) => ({
          title: data.title,
          styleType: data.type,
          message: data.message,
          onDismiss: () => onDismiss(entryId),
          id: entryId,
        })),
      [onDismiss],
    );

    const sortedInfobars = useMemo(
      (): InfobarType[] => {
        if (!isLoaded || !data || !Array.isArray(data)) {
          return [];
        }

        const visibleInfobars = data.filter(({ entryId }) => {
          const cookie = getCookie(`${COOKIE_GLOBAL_MSG}${entryId}`) as Record<string, any>;

          return !cookie || cookie.isVisible;
        });

        if (visibleInfobars.length === 0) {
          return [];
        }

        const entriesMapper = getEntriesMappedIntoInfobars(visibleInfobars);

        return [
          ...entriesMapper(CONTENT_INFOBAR_TYPE.error),
          ...entriesMapper(CONTENT_INFOBAR_TYPE.warning),
          ...entriesMapper(CONTENT_INFOBAR_TYPE.success),
          ...entriesMapper(CONTENT_INFOBAR_TYPE.info),
        ];
      },
      [isLoaded, data, getEntriesMappedIntoInfobars],
    );

    if (sortedInfobars.length === 0) {
      return null;
    }

    const injectedProps: InjectedProps = {
      infobars: sortedInfobars,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return WithContainer;
};
